import {getHtmlRequest, postRequest} from "../utils/fetch";
import {objectToQueryString} from "../utils/querystring";
import {ITabContentRequest} from "../components/molecules/tabs";
import {ICollapsibleContentRequest} from "../components/molecules/collapsable";

const basePath = "/apicore/contentapi/";
const showLabels = false;
const labels: StringKeyValuePair[] = [];
const icons: StringKeyValuePair[] = [];

export interface IKeyValueValuePair<TKey, TValue> {
	key: TKey;
	value: TValue;
}

export type StringKeyValuePair = IKeyValueValuePair<string, string>;

export interface IBaseContentRequest {
	language: string;
	pointOfSaleId: string;
}

export interface IBaseStepContentRequest extends IBaseContentRequest {
	preselectedOccasion?: string;
	productCategory: string;
	selectedAttributeValueMa: string[];
	stepIndex: number;
	optionGroup?: string;
	targetDeliveryDate?: string;
	purpose?: string;
	occasion?: string;
	theme?: string;
}

export interface IProductCategoryReviewsResult {
	readonly productCategoryReviews: IProductCategoryReview[];
	readonly totalAmountOfReviews: number;
	readonly starAmounts: { key: number, value: number }[];
}

export interface IContentRequest extends IBaseContentRequest{
	contentKeys: string[];
}

export interface IIconsRequest {
	contentKeys: string[];
}

export interface IProductCategoryReview {
	readonly score: number;
	readonly reviewDate: string;
	readonly formattedReviewDate: string;
	readonly reviewerFirstname: string;
	readonly reviewerLastname: string;
	readonly comment: string;
	readonly feedback: string;
	readonly feedbackDate: string;
	readonly formattedFeedbackDate: string;
	readonly reviewLanguageIso: string;
	readonly trustPilotDesignClass?: string;
}

export interface IProductCategoryReviewsRequest extends IBaseContentRequest {
	productCategoryId: string;
	page: number;
	stars?: number,
	reviewLanguage?: string;
	theme?: string
}

export function getCollapsibleContent(request: ICollapsibleContentRequest): Promise<string> {
	return getHtmlRequest(
		basePath + "getcollapsiblecontent" + objectToQueryString(request),
		{ headers: new Headers({'Content-type': 'application/json; charset=utf-8'}) })
		.then(res => {
			return res;
		}) as Promise<string>;
}

export function getLabelsAsync(keys: (string | undefined)[]) : Promise<StringKeyValuePair[]>{
	const missingKeys: string[] = [];
	const result: StringKeyValuePair[] = [];
	keys.forEach((key) => {
		if (!key)
			return;

		const label = getLabelByKey(key);
		if (label === null)
			missingKeys.push(key);
		else
			result.push({ key: key, value: label });
	});

	if (missingKeys.length > 0) {
		return postRequest<IContentRequest, StringKeyValuePair[]>(
			basePath + "getcontentlist",
			{
				pointOfSaleId: window.context.pointOfSaleId,
				language: window.context.language,
				contentKeys: missingKeys || []
			},
			{ headers: new Headers({'Content-type': 'application/json; charset=utf-8'}) })
			.then(res => {
				if (res) {
					addLabels(...res);
					return Promise.resolve(result.concat(res));
				}
				return Promise.resolve(result);
			}) as Promise<StringKeyValuePair[]>;
	}

	return Promise.resolve(result);
}

export function getIconsAsync(keys: (string | undefined)[]) : Promise<StringKeyValuePair[]>{
	const missingKeys: string[] = [];
	const result: StringKeyValuePair[] = [];
	keys.forEach((key) => {
		if (!key)
			return;

		const icon = getIconByKey(key);
		if (icon === null)
			missingKeys.push(key);
		else
			result.push({ key: key, value: icon });
	});

	if (missingKeys.length > 0) {
		return postRequest<IIconsRequest, StringKeyValuePair[]>(
			basePath + "GetIconsByName",
			{
				contentKeys: missingKeys || []
			},
			{ headers: new Headers({'Content-type': 'application/json; charset=utf-8'}) })
			.then(res => {
				if (res) {
					addIcons(...res);
					return Promise.resolve(result.concat(res));
				}
				return Promise.resolve(result);
			}) as Promise<StringKeyValuePair[]>;
	}

	return Promise.resolve(result);
}
export function getLabel(key: string, defaultContent = ""): string {
	if (showLabels)
		defaultContent = formatLabel(key, defaultContent);

	if (labels.length === 0)
		return defaultContent;

	return getLabelByKey(key) || defaultContent;
}

export function getIcon(key: string, defaultContent = ""): string {
	return getIconByKey(key) || defaultContent;
}

export function getTabContent(request: ITabContentRequest): Promise<string> {
	return getHtmlRequest(
		basePath + "gettabcontent" + objectToQueryString(request),
		{ headers: new Headers({'Content-type': 'application/json; charset=utf-8'}) })
		.then(res => {
			return res;
		}) as Promise<string>;
}

const getLabelByKey = (key: string) => {
	const filtered = labels
		.filter(keyVal => keyVal.key.toLowerCase() === key.toLowerCase())
		.map(p => p.value);

	if (filtered.length === 0)
		return null;

	return filtered[0];
};

const getIconByKey = (key: string) => {
	const filtered = icons
		.filter(keyVal => keyVal.key.toLowerCase() === key.toLowerCase())
		.map(p => p.value);

	if (filtered.length === 0)
		return null;

	return filtered[0];
};

const addLabels = (...labelsToAdd: StringKeyValuePair[]) => {
	labelsToAdd.forEach((label) => {
		if (!containsKey(label.key, labels)) {
			labels.push({ key: label.key, value: formatLabel(label.key, label.value) });
		}
	});
};

const addIcons = (...iconsToAdd: StringKeyValuePair[]) => {
	iconsToAdd.forEach((icon) => {
		if (!containsKey(icon.key, icons)) {
			icons.push({ key: icon.key, value: icon.value });
		}
	});
};

const containsKey = (key: string, source: StringKeyValuePair[]): boolean => {
	return source.filter(keyVal => keyVal.key === key).length > 0;
};

const formatLabel = (key: string, value: string): string => {
	value = value || "";
	return showLabels ? `[${key}]${value}` : value;
};


